import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { db } from '@/firebase';
import {
  CoParentingLink,
  SharedChild,
  CustodySwapRequest,
  SharedExpense,
  HandoffNote,
} from '@/types';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface CoParentingContextValue {
  link: CoParentingLink | null;
  children: SharedChild[];
  swapRequests: CustodySwapRequest[];
  expenses: SharedExpense[];
  handoffNotes: HandoffNote[];
  isLoaded: boolean;
  isLinked: boolean;
  myHouseholdId: string;
  otherHouseholdLabel: string;
  callCoParenting: (action: string, data?: Record<string, unknown>) => Promise<unknown>;
  unreadNoteCount: number;
}

const CoParentingContext = createContext<CoParentingContextValue>({
  link: null,
  children: [],
  swapRequests: [],
  expenses: [],
  handoffNotes: [],
  isLoaded: false,
  isLinked: false,
  myHouseholdId: '',
  otherHouseholdLabel: '',
  callCoParenting: async () => null,
  unreadNoteCount: 0,
});

export function CoParentingProvider({
  children: reactChildren,
  householdId,
  linkId,
}: {
  children: React.ReactNode;
  householdId: string | undefined;
  linkId: string | undefined;
}) {
  const [link, setLink] = useState<CoParentingLink | null>(null);
  const [kids, setKids] = useState<SharedChild[]>([]);
  const [swaps, setSwaps] = useState<CustodySwapRequest[]>([]);
  const [exps, setExps] = useState<SharedExpense[]>([]);
  const [notes, setNotes] = useState<HandoffNote[]>([]);
  const [isLoaded, setIsLoaded] = useState(!linkId);

  const unsubs = useRef<Array<() => void>>([]);

  useEffect(() => {
    // Clean up previous listeners
    unsubs.current.forEach(u => u());
    unsubs.current = [];

    if (!linkId) {
      setLink(null);
      setKids([]);
      setSwaps([]);
      setExps([]);
      setNotes([]);
      setIsLoaded(true);
      return;
    }

    setIsLoaded(false);
    let loadedCount = 0;
    const totalSubs = 5;
    const markLoaded = () => { loadedCount++; if (loadedCount >= totalSubs) setIsLoaded(true); };

    const linkRef = doc(db, 'coParenting', linkId);
    unsubs.current.push(onSnapshot(linkRef, snap => {
      setLink(snap.exists() ? ({ id: snap.id, ...snap.data() } as CoParentingLink) : null);
      markLoaded();
    }, () => markLoaded()));

    const sub = (col: string, setter: (v: any[]) => void) => {
      unsubs.current.push(onSnapshot(collection(db, 'coParenting', linkId, col), snap => {
        setter(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        markLoaded();
      }, () => markLoaded()));
    };

    sub('children', setKids);
    sub('swapRequests', setSwaps);
    sub('expenses', setExps);
    sub('handoffNotes', setNotes);

    return () => { unsubs.current.forEach(u => u()); unsubs.current = []; };
  }, [linkId]);

  const callCoParenting = async (action: string, data: Record<string, unknown> = {}) => {
    const fn = httpsCallable(getFunctions(), 'apiCoParenting');
    const result = await fn({ action, ...data });
    return result.data;
  };

  const myHouseholdId = householdId ?? '';
  const otherHouseholdLabel = link
    ? (link.householdA.householdId === myHouseholdId
        ? link.householdB?.label ?? 'Other Household'
        : link.householdA.label)
    : '';

  const unreadNoteCount = notes.filter(n => !n.readBy.includes(myHouseholdId)).length;

  return (
    <CoParentingContext.Provider value={{
      link,
      children: kids,
      swapRequests: swaps,
      expenses: exps,
      handoffNotes: notes,
      isLoaded,
      isLinked: !!link && link.status === 'active',
      myHouseholdId,
      otherHouseholdLabel,
      callCoParenting,
      unreadNoteCount,
    }}>
      {reactChildren}
    </CoParentingContext.Provider>
  );
}

export function useCoParenting() {
  return useContext(CoParentingContext);
}
